import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import { useAusschreibungen, toNum } from '@/hooks/useAusschreibungen';
import {
  FiInbox, FiLayers, FiCheckCircle, FiXCircle, FiRefreshCw, FiZap,
  FiAlertCircle, FiChevronRight, FiTrendingUp, FiClock, FiDollarSign,
  FiFileText, FiDroplet,
} from 'react-icons/fi';

/**
 * Abschluesse & Provisionen — die Uebersicht ueber alle Ausschreibungen.
 *
 * Eine Ausschreibung beginnt dort, wo die Vollmacht endet: mit ihr fragt der
 * Makler bei mehreren Versorgern konkrete Preise an, vergleicht die Rueckläufe,
 * waehlt den Gewinner und rechnet daraus die Provision aus.
 *
 * Fehlerregeln (bindend im Projekt):
 *   - loadError blendet Tabelle und Zahlen aus; Kennzahlen zeigen dann "–",
 *     niemals "0" — ein Fehler darf nie wie "keine Daten" aussehen.
 *   - writeError erscheint nur als Banner ueber weiterhin sichtbarem Inhalt.
 */

const STATUS = {
  offen:         { label: 'Offen',         cls: 'bg-gray-100 text-gray-700',   icon: FiInbox },
  angebote_da:   { label: 'Angebote da',   cls: 'bg-blue-100 text-blue-700',   icon: FiLayers },
  abgeschlossen: { label: 'Abgeschlossen', cls: 'bg-green-100 text-green-700', icon: FiCheckCircle },
  verloren:      { label: 'Verloren',      cls: 'bg-red-100 text-red-700',     icon: FiXCircle },
};

const MEDIUM = {
  strom: { label: 'Strom', cls: 'text-amber-600', icon: FiZap },
  gas:   { label: 'Gas',   cls: 'text-sky-600',   icon: FiDroplet },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('de-DE') : '–');

const fmtInt = (n) => {
  const v = toNum(n);
  return v === null ? '–' : v.toLocaleString('de-DE');
};

/** Geldbetrag, deutsch, immer zwei Nachkommastellen. */
const fmtEur = (n) => {
  const v = toNum(n);
  if (v === null) return '–';
  return `${v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

/** Preis in ct/kWh, deutsch, zwei Nachkommastellen. */
const fmtCt = (n) => {
  const v = toNum(n);
  if (v === null) return '–';
  return `${v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ct`;
};

const AbschluesseList = () => {
  const navigate = useNavigate();
  // loadError = Daten fehlen. writeError = Daten sind da, eine Aenderung schlug fehl.
  // Nur loadError darf die Tabelle ausblenden.
  const {
    ausschreibungen, loading, loadError, writeError, clearWriteError, stats, reload,
  } = useAusschreibungen();
  const [filter, setFilter] = useState('alle');

  // Solange nicht belastbar geladen wurde, darf keine Zahl behauptet werden.
  const zahlenUnsicher = loading || Boolean(loadError);
  const kennzahl = (v) => (zahlenUnsicher ? '–' : v.toLocaleString('de-DE'));
  const betrag = (v) => (zahlenUnsicher ? '–' : fmtEur(v));

  const visible = useMemo(
    () => (filter === 'alle'
      ? ausschreibungen
      : ausschreibungen.filter(a => (a.status || 'offen') === filter)),
    [ausschreibungen, filter]
  );

  // Erneutes Klicken auf die aktive Karte hebt den Filter wieder auf.
  const toggleFilter = (key) => setFilter(prev => (prev === key ? 'alle' : key));

  const filterKarten = [
    { key: 'offen',         label: 'Offen',         value: stats.offen,         icon: FiInbox,       tint: 'text-gray-500' },
    { key: 'angebote_da',   label: 'Angebote da',   value: stats.angebote_da,   icon: FiLayers,      tint: 'text-blue-600' },
    { key: 'abgeschlossen', label: 'Abgeschlossen', value: stats.abgeschlossen, icon: FiCheckCircle, tint: 'text-green-600' },
    { key: 'verloren',      label: 'Verloren',      value: stats.verloren,      icon: FiXCircle,     tint: 'text-red-600' },
  ];

  const provisionsKarten = [
    { key: 'erwartet',   label: 'Erwartet',   value: stats.provision_erwartet_eur,   icon: FiClock,       cls: 'text-gray-700',   hint: 'Vertrag steht, Provision noch nicht bestaetigt' },
    { key: 'bestaetigt', label: 'Bestaetigt', value: stats.provision_bestaetigt_eur, icon: FiTrendingUp,  cls: 'text-blue-700',   hint: 'Vom Versorger bestaetigt, noch nicht ueberwiesen' },
    { key: 'ausgezahlt', label: 'Ausgezahlt', value: stats.provision_ausgezahlt_eur, icon: FiCheckCircle, cls: 'text-green-700',  hint: 'Geld ist eingegangen' },
  ];

  const untertitel = () => {
    if (loading) return 'Lade …';
    if (loadError) return 'Daten konnten nicht geladen werden';
    if (filter !== 'alle') return `${visible.length} von ${stats.gesamt} Ausschreibungen · Filter: ${STATUS[filter]?.label || filter}`;
    return `${stats.gesamt} ${stats.gesamt === 1 ? 'Ausschreibung' : 'Ausschreibungen'}`;
  };

  return (
    <div className="space-y-6">
      {/* Kopf */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Abschluesse &amp; Provisionen</h1>
          <p className="text-gray-500 mt-1">{untertitel()}</p>
        </div>
        <div className="flex gap-2">
          {filter !== 'alle' && (
            <Button variant="ghost" onClick={() => setFilter('alle')}>Filter aufheben</Button>
          )}
          <Button variant="secondary" icon={FiRefreshCw} onClick={reload}>Aktualisieren</Button>
        </div>
      </div>

      {/* Ladefehler — keine stillen Altdaten, keine erfundenen Nullen */}
      {loadError && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <FiAlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-700">Ausschreibungen konnten nicht geladen werden</p>
              <p className="text-xs text-red-600/80 mt-0.5">{loadError}</p>
            </div>
            <button
              onClick={reload}
              className="flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:underline flex-shrink-0"
            >
              <FiRefreshCw size={13} /> Erneut versuchen
            </button>
          </div>
        </Card>
      )}

      {/* Schreibfehler — der Inhalt darunter bleibt sichtbar */}
      {writeError && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <FiAlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-700">Aenderung wurde nicht gespeichert</p>
              <p className="text-xs text-red-600/80 mt-0.5">{writeError}</p>
            </div>
            <button
              onClick={clearWriteError}
              className="text-xs font-semibold text-red-700 hover:underline flex-shrink-0"
            >
              Schliessen
            </button>
          </div>
        </Card>
      )}

      {/*
        Statuskarten = zugleich Filter.
        Achtung: dieses Projekt nutzt Tailwind v4 mit semantischen Farb-Tokens.
        Klassen wie `border-primary-500`/`bg-primary-50` erzeugen hier KEINE Regel
        (es gibt nur `primary`, `accent`, …) — deshalb Tokens ohne Zahl.
      */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {filterKarten.map((k) => (
          <button
            key={k.key}
            onClick={() => toggleFilter(k.key)}
            aria-pressed={filter === k.key}
            className={`text-left rounded-xl border p-4 transition-colors ${
              filter === k.key ? 'border-primary bg-accent text-accent-foreground' : 'border-border bg-card text-card-foreground hover:bg-muted/30'
            }`}
          >
            <div className="flex items-center gap-2 text-muted-foreground">
              <k.icon size={15} className={k.tint} />
              <span className="text-sm">{k.label}</span>
            </div>
            <p className="text-2xl font-bold mt-1 tabular-nums">{kennzahl(k.value)}</p>
          </button>
        ))}
      </div>

      {/* Provisionen */}
      <Card>
        <Card.Header>
          <div>
            <Card.Title>Provisionen</Card.Title>
            <Card.Description>Aus abgeschlossenen Ausschreibungen, ueber die gesamte Laufzeit</Card.Description>
          </div>
          <FiDollarSign size={18} className="text-gray-400" />
        </Card.Header>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {provisionsKarten.map((p) => (
            <div key={p.key} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-2 text-gray-500">
                <p.icon size={14} />
                <span className="text-sm">{p.label}</span>
              </div>
              <p className={`text-xl font-bold mt-1 tabular-nums ${p.cls}`}>{betrag(p.value)}</p>
              <p className="text-xs text-gray-500 mt-1">{p.hint}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabelle — bei Ladefehler bewusst gar nicht erst anzeigen */}
      {!loadError && (
        <Card padding={false}>
          <div className="overflow-x-auto">
            {loading ? (
              <p className="text-sm text-gray-400 py-12 text-center">Lade Ausschreibungen …</p>
            ) : visible.length === 0 ? (
              <div className="py-12 px-6 text-center">
                <FiFileText className="mx-auto text-gray-300 mb-3" size={32} />
                {stats.gesamt === 0 ? (
                  <>
                    <p className="text-gray-900 font-medium">Noch keine Ausschreibung angelegt</p>
                    <p className="text-sm text-gray-500 mt-2 max-w-xl mx-auto">
                      Eine <strong>Ausschreibung</strong> ist der Vorgang nach der Vollmacht: Sie fragen
                      damit bei mehreren Versorgern konkrete Preise an, sammeln deren Angebote hier an
                      einer Stelle, vergleichen sie und waehlen den Gewinner aus. Aus dem Gewinner-Angebot
                      ergeben sich Vertragslaufzeit und Ihre Provision.
                    </p>
                    <p className="text-sm text-gray-500 mt-3 max-w-xl mx-auto">
                      Angelegt wird sie im Kalkulator ueber <strong>„Versorger-Anfrage“</strong> — sobald
                      der Kunde die Preisspanne bestaetigt und die Vollmacht erteilt hat.
                    </p>
                    <Button
                      className="mt-4"
                      icon={FiZap}
                      onClick={() => navigate('/app/kalkulator')}
                    >
                      Zum Kalkulator
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-gray-900 font-medium">Keine Ausschreibungen in dieser Ansicht</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Kein Vorgang mit Status „{STATUS[filter]?.label || filter}“.
                    </p>
                    <Button className="mt-3" variant="secondary" onClick={() => setFilter('alle')}>
                      Alle anzeigen
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 bg-gray-50">
                    <th className="px-4 py-3">Nummer</th>
                    <th className="px-4 py-3">Kunde</th>
                    <th className="px-4 py-3">Medium</th>
                    <th className="px-4 py-3 text-right">Verbrauch</th>
                    <th className="px-4 py-3 text-right">Angebote</th>
                    <th className="px-4 py-3 text-right">Bester Preis</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Provision gesamt</th>
                    <th className="px-4 py-3">Angelegt</th>
                    <th className="px-4 py-3">Ersteller</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((a) => {
                    const st = STATUS[a.status] || STATUS.offen;
                    const med = MEDIUM[a.medium] || null;
                    const angefragt = toNum(a.angebote_anzahl);
                    const eingegangen = toNum(a.angebote_eingegangen);
                    return (
                      <tr
                        key={a.id}
                        onClick={() => navigate(`/app/abschluesse/${a.id}`)}
                        className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer group"
                      >
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                          {a.nummer || `#${a.id}`}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{a.company_name || '–'}</p>
                          <p className="text-xs text-gray-500">
                            {a.contact_name || ''}
                            {a.contact_name && a.plz ? ' · ' : ''}
                            {a.plz || ''}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          {med ? (
                            <span className={`inline-flex items-center gap-1 ${med.cls}`}>
                              <med.icon size={13} /> {med.label}
                            </span>
                          ) : (
                            <span className="text-gray-400">–</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 whitespace-nowrap">
                          {a.verbrauch_kwh == null ? '–' : `${fmtInt(a.verbrauch_kwh)} kWh`}
                        </td>
                        <td
                          className="px-4 py-3 text-right tabular-nums text-gray-700 whitespace-nowrap"
                          title={
                            angefragt === null
                              ? 'Keine Angaben zu Versorger-Anfragen'
                              : `${eingegangen ?? 0} eingegangen von ${angefragt} angefragt`
                          }
                        >
                          {angefragt === null ? '–' : (
                            <>
                              <span className={eingegangen ? 'font-semibold text-gray-900' : ''}>
                                {fmtInt(eingegangen ?? 0)}
                              </span>
                              <span className="text-gray-400"> / {fmtInt(angefragt)}</span>
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 whitespace-nowrap">
                          {fmtCt(a.bester_preis_ct_kwh)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                            <st.icon size={11} /> {st.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                          <span className={a.provision_gesamt_eur == null ? 'text-gray-400' : 'font-semibold text-gray-900'}>
                            {fmtEur(a.provision_gesamt_eur)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                        <td className="px-4 py-3 text-gray-600">{a.created_by_name || '–'}</td>
                        <td className="px-4 py-3 text-right">
                          <FiChevronRight
                            size={16}
                            className="text-gray-300 group-hover:text-primary transition-colors"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AbschluesseList;
